class PdpCaseSubscriptionUpsell extends HTMLElement {
  static SHOPLIFT_TEST_ID = "019dabcc-4604-7877-a1ba-45e1103c623f";

  async connectedCallback() {
    // Hide while Shoplift resolves to avoid flashing the upsell to control users
    this.style.display = "none";
    const isVariant = await window.shoplift?.isHypothesisActive(
      PdpCaseSubscriptionUpsell.SHOPLIFT_TEST_ID
    );
    if (!isVariant) {
      this.remove();
      return;
    }
    this.style.display = "";
    this.#mount();
  }

  #mount() {
    const dataEl = document.getElementById("case-subscription-upsell-data");
    if (!dataEl) return;

    this.data = JSON.parse(dataEl.textContent);
    this.checkbox = this.querySelector("#csu-toggle-input");
    this.priceEl = this.querySelector("#csu-price");
    this.compareEl = this.querySelector("#csu-compare");
    this.savingsEl = this.querySelector("#csu-savings");
    this.consentPriceEl = this.querySelector("#csu-consent-price");

    this.configEl =
      document.querySelector("main-product-system-configuration") ||
      document.querySelector("main-product-body-pillow-configuration");

    if (!this.configEl) return;

    this.isSystemConfig =
      this.configEl.tagName.toLowerCase() === "main-product-system-configuration";

    // Ensure the config element is upgraded before patching
    if (typeof this.configEl.addToCart !== "function") {
      customElements
        .whenDefined(this.configEl.tagName.toLowerCase())
        .then(() => this.init());
    } else {
      this.init();
    }
  }

  init() {
    // Patch addToCart to add the case-refresh marker as a subscription line.
    // Path C deferred billing: marker variant (price $0, requiresShipping=false)
    // is added with selling_plan attached so Shopify auto-creates a subscription
    // contract at checkout. That vaults the customer's payment method without
    // billing them. ~170d later the backend swaps the marker line for real case
    // variants on the contract (using the swatch properties stamped here) so
    // Shopify's native month-6 billing fires for the right cases at the right
    // price.
    const original = this.configEl.addToCart.bind(this.configEl);
    this.configEl.addToCart = (cartData) => {
      if (this.checkbox.checked) {
        const markerItem = this.buildMarkerItem();
        if (markerItem) cartData.items.push(markerItem);
      }
      return original(cartData);
    };

    this.updatePrice();
    this.checkbox.addEventListener("change", () => this.updatePrice());
  }

  /**
   * Builds the marker subscription line item. The marker variant carries the
   * subscription contract; swatch properties tell the backend executor which
   * real case variants to swap in before the contract's first real billing.
   * `_case_refresh` is the liquid-side detection marker for our custom cart UI
   * treatment; `_future_charge_cents` powers the "First charge in ~6 months"
   * price preview (the contract still bills via Shopify's selling plan, so this
   * is a UI-only display value sourced from the case variant's subscription
   * allocation, not a backend-enforced price).
   */
  buildMarkerItem() {
    const matches = this.resolveMatches();
    if (!matches?.primaryMatch) return null;
    if (!this.data.marker_variant_id || !this.data.selling_plan_id) return null;
    const { primaryMatch, secondaryMatch } = matches;
    const sellingPlanId = this.data.selling_plan_id;

    const subscriptionPriceCents = (match) => {
      if (!match) return 0;
      const alloc = match.variant.selling_plan_allocations?.find(
        (a) => a.selling_plan_id === sellingPlanId
      );
      return alloc ? alloc.per_delivery_price_raw : match.variant.price_raw;
    };
    const futureChargeCents =
      subscriptionPriceCents(primaryMatch) +
      subscriptionPriceCents(secondaryMatch);

    const properties = {
      _case_refresh: "v1",
      _consent_terms_version: this.data.consent_version || "v1",
      _primary_variant_id: String(primaryMatch.variant.id),
      _primary_swatch: primaryMatch.caseProduct.swatch?.label || "",
      _primary_image_url: primaryMatch.caseProduct.image || "",
      _future_charge_cents: String(futureChargeCents),
    };
    if (secondaryMatch) {
      properties._secondary_variant_id = String(secondaryMatch.variant.id);
      properties._secondary_swatch =
        secondaryMatch.caseProduct.swatch?.label || "";
    }

    return {
      id: this.data.marker_variant_id,
      quantity: 1,
      selling_plan: sellingPlanId,
      properties,
    };
  }

  /**
   * Resolves the primary + secondary case matches for the current size selection.
   * Coordinates color so we don't ship a mismatched set (e.g. Dove Gray wedge +
   * Pure White body pillow).
   */
  resolveMatches() {
    const sizeHandle = this.getCurrentSizeHandle();
    if (!sizeHandle) return null;

    const primaryMatch = this.findFirstAvailable(this.data.cases, sizeHandle);

    let secondaryMatch = null;
    if (this.data.secondary_cases && this.data.secondary_cases.length > 0) {
      const secondarySizeHandle = this.mapSecondarySize(sizeHandle);
      const primaryColor = primaryMatch?.caseProduct?.swatch?.label;
      const colorMatchedPool = primaryColor
        ? this.data.secondary_cases.filter(
            (c) => c.swatch?.label === primaryColor
          )
        : [];
      secondaryMatch =
        (colorMatchedPool.length > 0 &&
          this.findFirstAvailable(colorMatchedPool, secondarySizeHandle)) ||
        this.findFirstAvailable(this.data.secondary_cases, secondarySizeHandle);
    }

    return { primaryMatch, secondaryMatch };
  }

  getCurrentSizeHandle() {
    if (this.isSystemConfig) {
      const isBundleActive = this.configEl.querySelector(
        ".system-configuration-item.system-bundle-item.active"
      );
      const radio = this.configEl.querySelector(
        "product-system-size-selector input[type='radio']:checked"
      );
      if (!radio) return null;

      if (isBundleActive) {
        // Bundle radio values are already handleized sizes
        return radio.value;
      }

      // Standard system: value is variant ID — read size from label text
      const label = this.configEl.querySelector(
        `label[for="${radio.id}"]`
      );
      if (!label) return null;
      const sizeText = label.querySelector("span")?.textContent.trim();
      return sizeText
        ? sizeText.toLowerCase().replace(/\//g, "-").replace(/\s+/g, "-")
        : null;
    }

    // Body pillow: radio has data-size-handle
    const radio = this.configEl.querySelector(
      "body-pillow-size-selector input[type='radio']:checked"
    );
    return radio ? radio.dataset.sizeHandle : null;
  }

  findFirstAvailable(cases, sizeHandle) {
    for (const caseProduct of cases) {
      const variant = caseProduct.variants.find(
        (v) =>
          (v.size_handle === sizeHandle ||
            v.size_handle.startsWith(sizeHandle)) &&
          v.available
      );
      if (variant) return { caseProduct, variant };
    }
    return null;
  }

  /**
   * Maps a wedge/system size handle to the corresponding body pillow size.
   * Reflux: small → small, medium → medium-large, large → medium-large
   * Shoulder: small-medium → small, large → medium-large
   */
  mapSecondarySize(primarySizeHandle) {
    const map = {
      small: "small",
      medium: "medium-large",
      large: "medium-large",
      "small-medium": "small",
    };
    return map[primarySizeHandle] || primarySizeHandle;
  }

  updatePrice() {
    let totalFinal = 0;
    let totalCompare = 0;
    const planId = this.data.selling_plan_id;

    // Sum first-variant prices from each case group: subscription final + one-time compare
    [this.data.cases, this.data.secondary_cases].forEach((group) => {
      if (!group || group.length === 0) return;
      const firstProduct = group[0];
      if (!firstProduct || !firstProduct.variants.length) return;
      const v = firstProduct.variants[0];
      const alloc = v.selling_plan_allocations.find(
        (a) => a.selling_plan_id === planId
      );
      totalFinal += alloc ? alloc.per_delivery_price_raw : v.price_raw;
      totalCompare += v.price_raw;
    });

    if (totalFinal <= 0) return;

    this.priceEl.textContent = this.formatMoney(totalFinal);

    if (this.compareEl && totalCompare > totalFinal) {
      this.compareEl.textContent = this.formatMoney(totalCompare);
    }

    if (this.savingsEl && totalCompare > totalFinal) {
      const pct = Math.round(((totalCompare - totalFinal) / totalCompare) * 100);
      this.savingsEl.textContent = `Save ${pct}%`;
    }

    if (this.consentPriceEl) {
      this.consentPriceEl.textContent = this.formatMoney(totalFinal);
    }
  }

  formatMoney(cents) {
    return "$" + (cents / 100).toFixed(2).replace(/\.00$/, "");
  }
}

customElements.define(
  "pdp-case-subscription-upsell",
  PdpCaseSubscriptionUpsell
);
